import { useReducer, useCallback, useMemo } from 'react';

// Phase L6 — Designer history stack for {tree, events}.
//
// Cap of 50 entries per LAYOUTS_PLAN.md §6. Successive changes that pass the
// same coalesceKey within COALESCE_MS replace the top entry instead of
// pushing a new one — so dragging a slider or typing into a config field
// doesn't burn 50 history slots in two seconds.
//
// Scripts state is loaded from the layout but not mutated by the designer
// (script bodies live inside tree config), so it stays out of history.

const HISTORY_CAP = 50;
const COALESCE_MS = 500;

function pushOrCoalesce(state, nextValue, coalesceKey) {
  const now = Date.now();
  const top = state.history[state.index];
  if (top && coalesceKey != null && top.key === coalesceKey && (now - top.ts) < COALESCE_MS) {
    const next = state.history.slice();
    next[state.index] = { snapshot: nextValue, key: coalesceKey, ts: now };
    return { history: next, index: state.index, value: nextValue };
  }
  const trimmed = state.history.slice(0, state.index + 1);
  trimmed.push({ snapshot: nextValue, key: coalesceKey ?? null, ts: now });
  while (trimmed.length > HISTORY_CAP) trimmed.shift();
  return { history: trimmed, index: trimmed.length - 1, value: nextValue };
}

function reducer(state, action) {
  switch (action.type) {
    case 'set_tree': {
      const nextTree = typeof action.updater === 'function' ? action.updater(state.value.tree) : action.updater;
      return pushOrCoalesce(state, { ...state.value, tree: nextTree }, action.coalesceKey);
    }
    case 'set_events': {
      const nextEvents = typeof action.updater === 'function' ? action.updater(state.value.events) : action.updater;
      return pushOrCoalesce(state, { ...state.value, events: nextEvents }, action.coalesceKey);
    }
    case 'set_both':
      return pushOrCoalesce(state, { tree: action.tree, events: action.events }, action.coalesceKey);
    case 'undo':
      if (state.index <= 0) return state;
      return { ...state, index: state.index - 1, value: state.history[state.index - 1].snapshot };
    case 'redo':
      if (state.index >= state.history.length - 1) return state;
      return { ...state, index: state.index + 1, value: state.history[state.index + 1].snapshot };
    case 'reset':
      return {
        history: [{ snapshot: action.value, key: null, ts: Date.now() }],
        index: 0,
        value: action.value,
      };
    default:
      return state;
  }
}

function makeInitial(value) {
  return {
    history: [{ snapshot: value, key: null, ts: Date.now() }],
    index: 0,
    value,
  };
}

export default function useDesignerHistory(initialValue) {
  const [state, dispatch] = useReducer(reducer, undefined, () => makeInitial(initialValue));

  // All setters are stable across re-renders — the reducer pulls fresh state
  // from its closure on each dispatch, so callers can hold the same setter
  // reference without going stale.
  const setTree = useCallback((updater, coalesceKey = null) => {
    dispatch({ type: 'set_tree', updater, coalesceKey });
  }, []);
  const setEvents = useCallback((updater, coalesceKey = null) => {
    dispatch({ type: 'set_events', updater, coalesceKey });
  }, []);
  const setBoth = useCallback((nextTree, nextEvents, coalesceKey = null) => {
    dispatch({ type: 'set_both', tree: nextTree, events: nextEvents, coalesceKey });
  }, []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const reset = useCallback((value) => dispatch({ type: 'reset', value }), []);

  return useMemo(() => ({
    tree: state.value.tree,
    events: state.value.events,
    setTree,
    setEvents,
    setBoth,
    undo,
    redo,
    reset,
    canUndo: state.index > 0,
    canRedo: state.index < state.history.length - 1,
    historyIndex: state.index,
    historySize: state.history.length,
  }), [state, setTree, setEvents, setBoth, undo, redo, reset]);
}
